<script module>
	import MarkdownIt from 'markdown-it';

	export const parser = new MarkdownIt({
		html: true,
		linkify: true,
		typographer: true
	});
</script>

<script lang="ts">
	/* eslint-disable svelte/no-at-html-tags */

	import { cubicIn, cubicOut } from 'svelte/easing';
	import { fade, fly } from 'svelte/transition';

	import { trimTextForMetaDescription } from '$lib/domain/text';
	import { photographPath } from '$lib/reactive/photograph';

	import HandwrittenHeader from '$lib/components/HandwrittenHeader.svelte';
	import ContactForm from '$lib/components/ContactForm.svelte';

	import { page } from '$app/state';

	import { readFragment } from '$lib/graphql';
	import type { PageData } from './$types';
	import { t, locale, path } from '$lib/translations';
	import { formatAsLong } from '$lib/date-and-time';
	import { ƒ_photographs } from '$lib/routes';

	import StaticMap from '$lib/components/StaticMap.svelte';
	import ExifBox from '$lib/components/photograph/ExifBox.svelte';
	import PhotographPermalink from '$lib/components/photograph/PhotographPermalink.svelte';
	import Photograph from '$lib/components/photograph/PhotographPhotograph.svelte';
	import { PhotographPhotographFragment } from '$lib/components/photograph/PhotographPhotograph.fragment';
	import Tags from '$lib/components/post-header/Tags.svelte';
	import Wrapper from '$lib/components/post-header/Wrapper.svelte';
	import Category from '$lib/components/post-header/Category.svelte';
	import Timestamp from '$lib/components/post-header/Timestamp.svelte';
	import Title from '$lib/components/post-header/Title.svelte';

	let formatToLocaleAsLong = $derived(formatAsLong($locale));

	interface Props {
		data: PageData;
	}

	let { data }: Props = $props();

	let { current, next, previous } = $derived(data);

	let photograph = $derived(readFragment(PhotographPhotographFragment, current.photograph));

	let responsiveImage = $derived(photograph?.responsiveImage);

	let style = $derived(`background-image: url(${responsiveImage?.base64})`);

	let description = $derived(
		trimTextForMetaDescription(160)(
			parser
				.render(current?.text || '')
				.split('\n')
				.slice(0, 2)
				.map((str) => str.replace(/<\/?[^>]+(>|$)/g, ''))
				.join(' ')
		) || current?.title
	);

	const duration = 300;
	const x = -100;

	// See: https://joshcollinsworth.com/blog/great-transitions
	const transitionIn = () => ({ easing: cubicOut, x, duration, delay: 400 });
	const transitionOut = () => ({ easing: cubicIn, x, duration });
</script>

<svelte:head>
	<link rel="canonical" href={`https://www.sistrall.it${$photographPath(current)}`} />
	<title>{current?.title} — sistrall.it</title>
	<meta name="description" content={current?.seo?.description || description} />
	<meta property="og:title" content={current?.title} />
	<meta property="og:url" content={`https://www.sistrall.it${$photographPath(current)}`} />
	<meta property="og:type" content="article" />
	<meta property="twitter:card" content="summary_large_image" />
	<meta property="twitter:description" content={current?.seo?.description || description} />
	<meta property="twitter:url" content={`https://www.sistrall.it${$photographPath(current)}`} />
	<meta property="og:image" content={`${responsiveImage.src}`} />
	<meta property="og:image:width" content={`${responsiveImage.width}`} />
	<meta property="og:image:height" content={`${responsiveImage.height}`} />
	<meta property="og:image:alt" content="" />
	<meta property="twitter:image" content={`${responsiveImage.src}`} />

	{#if responsiveImage && previous}
		<link rel="preload" as="image" href={responsiveImage.src} imagesizes={responsiveImage.sizes} imagesrcset={responsiveImage.srcSet} />
	{/if}
</svelte:head>

{#if responsiveImage}
	<div class="mb-4 overflow-hidden rounded-t-md bg-cover transition-all grid grid-cols-1 grid-rows-1" {style}>
		{#if previous}
			{#key responsiveImage.src}
				<span transition:fade>
					<PhotographPermalink photograph={previous} class="grid grid-cols-1 grid-rows-1" style="grid-column: 1; grid-row: 1;" data-sveltekit-noscroll>
						<Photograph photograph={current.photograph} />
					</PhotographPermalink>
				</span>
			{/key}
		{:else}
			<Photograph photograph={current.photograph} />
		{/if}
	</div>
{/if}

<div class="mx-auto max-w-5xl px-2 md:px-4 lg:px-8">
	<HandwrittenHeader />

	<div class="stabilize-when-animate">
		{#key $locale}
			<div in:fly={transitionIn()} out:fly={transitionOut()}>
				<header>
					<Wrapper>
						<div>
							<Category>
								<a href={$path(ƒ_photographs)}>{$t('photograph.photo')}</a>
							</Category>
							{#if current?._firstPublishedAt}
								<Timestamp>
									{@html $t('photograph.publishedOn', { date: formatToLocaleAsLong(new Date(current._firstPublishedAt)) })}
								</Timestamp>
							{/if}
							<Tags>
								{#each current.tags as { name }}
									{name}{' '}
								{/each}
							</Tags>
						</div>
						<Title>
							<h1>{current?.title}</h1>
						</Title>
					</Wrapper>
				</header>

				<div class="prose [&_a]:animated-underline-sage-green px-2 md:px-4 lg:px-8 [&_a]:font-bold [&_a]:underline [&_a]:underline-offset-4">
					{@html current?.text}
				</div>

				{#if current.photograph.exifInfo}
					<div class="my-8 max-w-prose px-2 md:px-4 lg:px-8">
						<ExifBox exifInfo={current.photograph.exifInfo} />
					</div>
				{/if}

				{#if current?.location}
					<div class="my-8 max-w-prose px-2 md:px-4 lg:px-8">
						<StaticMap location={current?.location} />
					</div>
				{/if}

				<div class="my-4 max-w-prose px-2 md:my-4 md:px-4 lg:my-8 lg:px-8">
					<ContactForm referer={page.url.href} cutoffDate={new Date(current._firstPublishedAt)} />
				</div>

				<div class="py-8 grid grid-cols-2 gap-12">
					<div>
						{#if previous}
							<div>
								<p class="ml-2 inline-block border-b-4 font-sans font-bold uppercase md:ml-4 lg:ml-8">{$t('photograph.previous')}</p>
								<div class="border-t px-2 md:px-4 lg:px-8">
									<PhotographPermalink photograph={previous}>
										<img
											class="aspect-square w-full rounded-b-lg object-cover"
											src={previous.photograph?.responsiveImage?.src}
											sizes={previous.photograph?.responsiveImage?.sizes}
											srcset={previous.photograph?.responsiveImage?.srcSet}
											alt="Miniatura della fotografia precedente"
										/>
									</PhotographPermalink>
									<p>
										{@html $t('photograph.publishedOn', { date: formatToLocaleAsLong(new Date(previous._firstPublishedAt)) })}
									</p>
								</div>
							</div>
						{:else if next}
							<div>
								<p class="ml-2 inline-block border-b-4 font-sans font-bold uppercase md:ml-4 lg:ml-8">The End, la fine</p>
								<p class="border-t px-2 md:px-4 lg:px-8">
									<PhotographPermalink photograph={next}>Torna indietro: non c'è più niente, per di qua.</PhotographPermalink>
								</p>
							</div>
						{/if}
					</div>

					<div>
						{#if next}
							<div>
								<p class="ml-2 inline-block border-b-4 font-sans font-bold uppercase md:ml-4 lg:ml-8">{$t('photograph.next')}</p>
								<div class="border-t px-2 md:px-4 lg:px-8">
									<PhotographPermalink photograph={next}>
										<img
											class="aspect-square w-full rounded-b-lg object-cover"
											src={next.photograph?.responsiveImage?.src}
											sizes={next.photograph?.responsiveImage?.sizes}
											srcset={next.photograph?.responsiveImage?.srcSet}
											alt="Miniatura della fotografia successiva"
										/>
									</PhotographPermalink>
									<p>
										{@html $t('photograph.publishedOn', { date: formatToLocaleAsLong(new Date(next._firstPublishedAt)) })}
									</p>
								</div>
							</div>
						{:else if previous}
							<div>
								<p class="ml-2 inline-block border-b-4 font-sans font-bold uppercase md:ml-4 lg:ml-8">The End, la fine</p>
								<p class="border-t px-2 md:px-4 lg:px-8">
									<PhotographPermalink photograph={previous}>Torna indietro: non c'è più niente, per di qua.</PhotographPermalink>
								</p>
							</div>
						{/if}
					</div>
				</div>
			</div>
		{/key}
	</div>
</div>
